<template>
<Card
    class="h-100 w-100"
    body-classes="d-flex flex-column justify-content-between align-items-center"
    header-classes="p-0 d-flex justify-content-center align-items-center"
>
    <template #header><div
        class="w-100 p-4 text-center rounded-top"
        :style="$bgColor(color.primary)"
    >
        <h3
            class="card-title mt-2 mb-0"
            :style="{ color: color.secondary }"
        >{{ plan.translation.name }}</h3>
    </div></template>
    <div class="nch--image-plan d-flex align-items-center justify-content-center">
        <ImageItem
            :placeholder-w="300"
            :placeholder-h="225"
            :source="cardImage"
            alt="card-image"
        ></ImageItem>
    </div>
    <div class="w-100">
        <base-progress
            class="progress-filter"
            show-label
            :type="color.primary"
            :height="8"
            :value="plan.progress"
            :label="$t('dashboard.progress')"
        ></base-progress>
    </div>
    <div
        @mouseover="showTooltip=true"
        @mouseleave="showTooltip=false"
        v-if="this.plan.total_expiration_days" class="w-100"
    >
        <base-progress
            class="progress-filter"
            show-label
            :type="color.primary"
            :height="8"
            :value="calcPercentageExpiration"
            :label="$t('dashboard.expiration_days')"
            :justColorBar="statusColorExpiration"
        >
            <div>
                <span>{{ expirationDays }}</span>
                <span
                    v-if="plan.current_expiration_days > 1"
                >
                    {{$t('common.days')}}
                </span>
            </div>
            <BaseTooltip
                class="tooltip-position"
                :toggle="showTooltip"
                :color-primary="color.primary"
                :color-secondary="color.secondary"
                direction="bottom"
                :padding="3"
            >
                {{ `${$t('dashboard.expiration_date')}: ${$moment(plan.expiration_date).format('DD/MM/YYYY')}` }}
            </BaseTooltip>
        </base-progress>
    </div>
    <div
        class="w-100 d-flex flex-column"
        v-if="released"
    >
        <span
            class="align-self-start bg-warning text-secondary px-2 rounded"
        >
            {{ $t('dashboard.plan.unavailable_content') }}
        </span>
        <span
            class="align-self-end"
        >
            {{ $t('dashboard.plan.release_date') }}
            <b>{{ $moment(released).format('DD/MM/YYYY') }}</b>
        </span>
    </div>
    <template #footer><div class="d-flex justify-content-between">
        <div
            data-placement="bottom"
            data-toggle="tooltip"
            :title="plan.lock_message ? $t(plan.lock_message) : ''"
        >
            <BaseButton
                :style="button.style"
                :disabled="button.disabled"
                :loading="loading"
                @click="button.onClick"
            >{{ button.text }}</BaseButton>
        </div>
        <BaseButton
            tag="a"
            :href="detailsLink"
            type="secondary"
            :style=" [
                { color: layoutButtons.details.color,
                'font-family': layoutButtons.details.font },
                $bgColor(layoutButtons.details.background),
            ]"
        >{{ layoutButtons.details.translation }}</BaseButton>
    </div></template>

    <Modal v-model:show="confirmModal" body-classes="p-0">
        <Card
            class="mb-0"
            header-classes="p-0 rounded-top"
            footer-classes="d-flex align-items-center justify-content-end"
        >
            <template #header><div class="py-4 px-3" :style="$bgColor(color.primary)">
                <h6
                    class="modal-title"
                    id="modal-title-default"
                    :style="{color: color.secondary}"
                >
                    <i class="fas fa fa-check-circle mr-2"></i>
                    {{$t('dashboard.plan.external_status_title')}}
                </h6>
            </div></template>

            <div class="d-flex flex-column align-items-center justify-content-center">
                <p
                    class="text-center"
                    v-html="$t('dashboard.plan.external_status_description',
                        { name: this.user.name })"
                ></p>
            </div>

            <template #footer>
                <base-button
                    type="outline-primary"
                    :style="$bgColor(color.secundary)"
                    @click="confirmModal = false"
                >{{$t('common.close')}}</base-button>
                <base-button
                    tag="a"
                    :href="this.$getSettings.settings.student_plan_external_link"
                    class="ml-auto text-white"
                    :style="$bgColor(color.primary)"
                >{{$t('dashboard.plan.external_status_ok')}}</base-button>
            </template>
        </Card>
    </Modal>
</Card>
</template>

<script>
import image from '../../mixins/image';

import ImageItem from '../Common/ImageItem';

import Modal from '../../argon/components/Modal';
import Card from '../../argon/components/Cards/Card';
import BaseButton from '../../argon/components/BaseButton';
import BaseProgress from '../../argon/components/BaseProgress';
import BaseTooltip from '../../argon/components/BaseTooltip';

export default {
    name: 'DashboardPlanCard',
    mixins: [image],

    components: {
        Modal,
        Card,
        BaseButton,
        BaseProgress,
        ImageItem,
        BaseTooltip,
    },

    props: {
        plan: {
            type: Object,
            required: true,
        },
        color: {
            type: Object,
            required: true,
        },
        layoutButtons: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            confirmModal: false,
            loading: false,
            showTooltip: false,
            released: this.plan.release_date,
        };
    },

    methods: {
        getLastAccessContent() {
            this.loading = true;
            return window.axios
                .get(`/api/app/plan/${this.plan.id}/continue`)
                .then(res => res.data.link)
                .catch(err => console.log(err));
        },
    },

    computed: {
        detailsLink() {
            if (!this.plan.status || this.plan.lock) {
                return `/api/public/details/plan/${this.plan.id}`;
            }
            return `/app/plan/${this.plan.id}`;
        },
        user() {
            return this.$store.getters.USER;
        },
        cardImage() {
            if (this.plan.card_img) {
                return this.image(this.plan.card_img, 'thumb');
            }
            return '/img/img-card-plan.jpg';
        },
        button() {
            switch (true) {
            case !!this.released:
                return {
                    text: this.$t('dashboard.disabled'),
                    href: `/app/plan/${this.plan.id}`,
                    disabled: true,
                    style: [
                        { color: this.color.secondary },
                        this.$bgColor(this.color.primary),
                    ],
                    onClick: () =>
                        (location.href = `/app/plan/${this.plan.id}`),
                };
            case this.plan.status && !this.plan.lock && this.plan.started:
                return {
                    text: this.layoutButtons.continue.translation,
                    disabled: false,
                    style: [
                        { color: this.layoutButtons.continue.color,
                            'font-family': this.layoutButtons.continue.font },
                        this.$bgColor(this.layoutButtons.continue.background),
                    ],
                    onClick: async () => {
                        await this.getLastAccessContent().then(
                            link => (location.href = link),
                        );
                    },
                };
                break;
            case this.plan.status && !this.plan.lock && !this.plan.started:
                return {
                    text: this.layoutButtons.start.translation,
                    href: `/app/plan/${this.plan.id}`,
                    disabled: false,
                    style: [
                        { color: this.layoutButtons.start.color,
                            'font-family': this.layoutButtons.start.font },
                        this.$bgColor(this.layoutButtons.start.background),
                    ],
                    onClick: () =>
                        (location.href = `/app/plan/${this.plan.id}`),
                };
                break;

            case !this.plan.status:
                return {
                    text: this.$t('dashboard.disabled'),
                    href: '/app/dashboard',
                    disabled: true,
                    style: [this.$bgColor('red'), { opacity: '0.40' }],
                    onClick: () => (location.href = '/app/dashboard'),
                };

            case this.plan.lock_type === 'WAITING_EXTERNAL_STATUS':
                return {
                    text: this.$t('dashboard.access'),
                    href: undefined,
                    disabled: false,
                    style: [
                        { color: this.color.secondary },
                        this.$bgColor(this.color.primary),
                    ],
                    onClick: e => (this.confirmModal = true),
                };

            case this.plan.lock_type === 'WAITING_AUTHORIZATION':
                return {
                    text: this.$t('dashboard.disabled'),
                    href: `/app/plan/${this.plan.id}`,
                    disabled: true,
                    style: [this.$bgColor('red'), { opacity: '0.40' }],
                    onClick: () =>
                        (location.href = `/app/plan/${this.plan.id}`),
                };

            default:
                return {
                    text: this.$t('dashboard.disabled'),
                    href: `/app/plan/${this.plan.id}`,
                    disabled: true,
                    style: [
                        { color: this.color.secondary },
                        this.$bgColor(this.color.primary),
                    ],
                    onClick: () =>
                        (location.href = `/app/plan/${this.plan.id}`),
                };
            }
        },
        calcPercentageExpiration() {
            if (this.plan.current_expiration_days !== -1) {
                return (this.plan.current_expiration_days * 100) / this.plan.total_expiration_days;
            }
            return 0;
        },
        statusColorExpiration() {
            if (this.calcPercentageExpiration < 34) {
                return 'danger';
            } else if (this.calcPercentageExpiration > 34 && this.calcPercentageExpiration < 67) {
                return 'warning';
            }
            return 'success';
        },
        expirationDays() {
            if (this.plan.current_expiration_days > 1) {
                return this.plan.current_expiration_days;
            } else if (this.plan.current_expiration_days === 1) {
                return this.$t('dashboard.tomorrow');
            } else if (this.plan.current_expiration_days === 0) {
                return this.$t('dashboard.today');
            }
            return this.$t('dashboard.expired');
        },
    },
};
</script>

<style >
img {
    max-width: 100%;
    height: auto;
}
.nch--image-plan {
    width: 300px;
    height: 300px;
}

.text-expiration{
    width: 100%;
    display: flex;
}

.progress-filter .progress .progress-bar {
    filter: contrast(0.5)
}

.tooltip-position {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
}
</style>
